import { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Divider,
} from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { LsButton } from '../../../../core';
import LsChip from '../../../../core/LsChip';
import LsDialog from '../../../../core/LsDialog';
import LsTable from '../../../../core/LsTable';
import { getFormSubmisisonData } from '../../../../service/api/FormSubmissionService';

// ── Helpers ──────────────────────────────────────────────────
const formatDateTime = (value) => {
    if (!value) return '—';
    return new Date(value).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

// Recursively render a value — handles nested objects/arrays gracefully
const renderValue = (value) => {
    if (value === null || value === undefined)
        return <Typography variant="body2" color="text.disabled">—</Typography>;

    if (typeof value === 'boolean') {
        return (
            <LsChip
                label={value ? 'Yes' : 'No'}
                color={value ? 'success' : 'default'}
                variant="outlined"
                size="small"
            />
        );
    }

    if (Array.isArray(value)) {
        if (value.length === 0)
            return <Typography variant="body2" color="text.disabled">—</Typography>;
        return (
            <Box display="flex" flexWrap="wrap" gap={0.5}>
                {value.map((item, i) => (
                    <LsChip
                        key={i}
                        label={typeof item === 'object' ? JSON.stringify(item) : String(item)}
                        variant="outlined"
                        size="small"
                    />
                ))}
            </Box>
        );
    }

    if (typeof value === 'object') {
        return (
            <Box
                component="pre"
                sx={{
                    m: 0,
                    p: 1,
                    backgroundColor: '#f5f7fa',
                    borderRadius: 1,
                    fontSize: '0.75rem',
                    color: 'text.secondary',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    maxHeight: 120,
                    overflowY: 'auto',
                }}
            >
                {JSON.stringify(value, null, 2)}
            </Box>
        );
    }

    return (
        <Typography variant="body2" color="text.primary" sx={{ wordBreak: 'break-word' }}>
            {String(value)}
        </Typography>
    );
};

// Prettify camelCase / snake_case keys → "Submitted Data Key"
const formatKey = (key) =>
    key
        .replace(/([A-Z])/g, ' $1')
        .replace(/_/g, ' ')
        .replace(/^\w/, (c) => c.toUpperCase())
        .trim();

// ── Meta info row ────────────────────────────────────────────
const MetaItem = ({ icon, label, value }) => (
    <Box display="flex" alignItems="center" gap={0.75}>
        {icon}
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
            {label}:
        </Typography>
        <Typography variant="caption" color="text.primary">
            {value}
        </Typography>
    </Box>
);

// ── Main modal ───────────────────────────────────────────────
export default function SubmissionDetailModal({ submissionId, open, onClose }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open || !submissionId) return;
        let cancelled = false;
        setData(null);
        setError(null);
        setLoading(true);

        getFormSubmisisonData(submissionId)
            .then((res) => { if (!cancelled) setData(res); })
            .catch((err) => {
                if (!cancelled)
                    setError(typeof err === 'string' ? err : err?.message || 'Failed to load submission.');
            })
            .finally(() => { if (!cancelled) setLoading(false); });

        return () => { cancelled = true; };
    }, [open, submissionId]);

    const formEntries = data?.submittedData ? Object.entries(data.submittedData) : [];

    return (
        <LsDialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            disableScrollLock
            title={
                <Box display="flex" alignItems="center" gap={1}>
                    <DescriptionOutlinedIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography variant="h6" fontWeight={700} fontSize="1rem">
                        Form Submission
                    </Typography>
                    {data?.id && (
                        <LsChip
                            label={`#${data.id}`}
                            variant="outlined"
                            size="small"
                            sx={{ ml: 0.5 }}
                        />
                    )}
                </Box>
            }
            actions={
                <LsButton variant="outlined" color="inherit" onClick={onClose} size="small">
                    Close
                </LsButton>
            }
        >
            {/* Loading */}
            {loading && (
                <Box display="flex" alignItems="center" justifyContent="center" py={6} gap={1.5}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">Loading submission…</Typography>
                </Box>
            )}

            {/* Error */}
            {error && !loading && (
                <Box py={4} textAlign="center">
                    <Typography variant="body2" color="error.main">{error}</Typography>
                </Box>
            )}

            {/* Content */}
            {data && !loading && (
                <>
                    {/* Meta strip */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                            mb: 2.5,
                            px: 2,
                            py: 1.5,
                            backgroundColor: '#fafafa',
                            borderRadius: 2,
                            border: '1px solid #e0e0e0',
                        }}
                    >
                        <MetaItem
                            icon={<PersonOutlineIcon sx={{ fontSize: 14, color: 'text.secondary' }} />}
                            label="User ID"
                            value={data.userId || '—'}
                        />
                        <MetaItem
                            icon={<AccessTimeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />}
                            label="Submitted"
                            value={formatDateTime(data.createdAt)}
                        />
                        <MetaItem
                            icon={<AccessTimeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />}
                            label="Last Updated"
                            value={formatDateTime(data.updatedAt)}
                        />
                    </Box>

                    <Divider sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600} letterSpacing="0.06em" textTransform="uppercase">
                            Submitted Data
                        </Typography>
                    </Divider>

                    {/* Form data table */}
                    {formEntries.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
                            No form data available.
                        </Typography>
                    ) : (
                        <LsTable
                            columns={[
                                {
                                    field: 'field',
                                    headerName: 'Field',
                                    width: '38%',
                                    renderCell: (row) => (
                                        <Typography
                                            variant="caption"
                                            fontWeight={600}
                                            color="text.secondary"
                                            sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}
                                        >
                                            {row.field}
                                        </Typography>
                                    ),
                                },
                                {
                                    field: 'value',
                                    headerName: 'Value',
                                    renderCell: (row) => renderValue(row.rawValue),
                                },
                            ]}
                            rows={formEntries.map(([key, value]) => ({
                                id: key,
                                field: formatKey(key),
                                rawValue: value,
                            }))}
                            dense
                        />
                    )}
                </>
            )}
        </LsDialog>
    );
}